import React, { memo, useEffect, useCallback, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { EmptyFavoritesMessage } from 'features/favorites';
import { GameCard } from 'features/games';
import type IGameSettings from 'types/models/IGameSettings';

import { useOpenType } from 'core/providers/OpenTypeContext';
import { LeftArrow, RightArrow } from '../../../layouts/Section/ScrollArrows';
import { GenericErrorBoundary } from '../ErrorBoundaries/GenericErrorBoundary';
import { GameCardFallback } from '../ErrorBoundaries/GameCardErrorBoundary/GameCardFallback';
import { HORIZONTAL_GRID_GAME_CARD_SIZE, type HorizontalGridCardSize, useHorizontalGridDimensions } from './hooks/useHorizontalGridDimensions';
import { useIsRowNearViewport } from './hooks/useIsRowNearViewport';
import { useScrollArrows } from './hooks/useScrollArrows';

import styles from './ReactVirtualGrid.module.css';

/**
 * Cards built ahead of the visible range as the row is swiped right.
 *
 * The row loads lazily to the right only: every card up to the furthest one reached stays
 * mounted, so swiping back left finds them already there instead of rebuilding them. This
 * buffer covers the beat the virtualizer's range lags behind a fling on iOS, which is what
 * would otherwise show as an empty slot.
 *
 * This is the horizontal half of the lead time: it decides how far off the side of the row
 * a card is built, so swiping sideways finds it ready rather than loading. The vertical
 * half lives in `useIsRowNearViewport`.
 *
 * Costed differently from the vertical grid's window, though: a row is never unmounted
 * once built, so in multi view every card of overscan is paid once per section for the
 * rest of the session, while the vertical grid pays its window once. That is why this is
 * the smaller of the two numbers.
 */
const CARD_OVERSCAN = 5;

interface ReactVirtualGridProps {
  games: IGameSettings[];
  isFav: boolean;
  sectionName: string;
  hasJackpotPanel: boolean;
  gameCardSize?: HorizontalGridCardSize;
  ref?: React.Ref<HTMLDivElement>;
}

const GameItem = memo(
  ({
    index,
    games,
    sectionName,
    style,
    isLast,
  }: {
    index: number;
    games: IGameSettings[];
    sectionName: string;
    style: React.CSSProperties;
    isLast: boolean;
  }) => {
    const game = games[index];
    const wrapperClassName = `${styles.gameItemWrapper}${isLast ? ` ${styles.lastItem}` : ''}`;

    if (!game) {
      return <div style={style} className={wrapperClassName} />;
    }

    return (
      <div style={style} className={wrapperClassName}>
        <GenericErrorBoundary
          key={`${game.gameKey}_${index}`}
          type="game"
          identifier={`GameCard-${game.gameKey}`}
          fallback={(error, reset) => <GameCardFallback gameName={game.name} error={error} onRetry={reset} />}
        >
          <GameCard data={game} sectionName={sectionName} displayIndex={index + 1} sectionGames={games} />
        </GenericErrorBoundary>
      </div>
    );
  },
);

GameItem.displayName = 'GameItem';

const ReactVirtualGrid = ({
  games,
  isFav,
  sectionName,
  hasJackpotPanel,
  gameCardSize = HORIZONTAL_GRID_GAME_CARD_SIZE.DEFAULT,
  ref,
}: ReactVirtualGridProps) => {
  const { openType } = useOpenType();
  const scrollElementRef = useRef<HTMLDivElement>(null);

  const { itemWidth, listHeight, getGap, getPadding } = useHorizontalGridDimensions(hasJackpotPanel, gameCardSize);

    // Sections are never unmounted once built, so without this a row ten screens away
    // still holds a full window of decoded thumbnails for the rest of the session.
    // `itemWidth` changes with the viewport, which is exactly when the window needs
    // re-measuring.
    const isRowNear = useIsRowNearViewport(scrollElementRef, itemWidth);

    const virtualizer = useVirtualizer({
      count: games.length,
      getScrollElement: () => scrollElementRef.current,
      estimateSize: useCallback(() => itemWidth, [itemWidth]),
      horizontal: true,
      overscan: CARD_OVERSCAN,
    });

  useEffect(() => {
    virtualizer.measure();
  }, [itemWidth, virtualizer]);

    // Furthest card the row has reached. Cards up to it stay mounted, so swiping back left
    // doesn't rebuild what was already shown - only swiping right loads new ones.
    const windowEnd = virtualizer.getVirtualItems().at(-1)?.index ?? -1;
    const [reachedEnd, setReachedEnd] = useState(-1);
    if (windowEnd > reachedEnd) setReachedEnd(windowEnd);

  const { scrollArrows, handleMouseEnter, handleMouseLeave, scrollLeft, scrollRight } = useScrollArrows({
    scrollElementRef,
    gamesLength: games.length,
    itemWidth,
    getPadding,
    getGap,
  });

  if (games.length === 0) {
    return isFav ? (
      <div
        className={styles.emptyContainer}
        data-has-jackpot={hasJackpotPanel}
        data-section-name={sectionName}
        data-is-favorites="true"
        open-type={openType}
        ref={ref}
        style={{ width: '100%', padding: `0 ${getPadding().start}px` }}
      >
        <EmptyFavoritesMessage />
      </div>
    ) : null;
  }

    // Out of range the row renders nothing. The spacer below keeps its width from the
    // virtualizer's total size and the scroller keeps its fixed height, so the row's
    // geometry and its horizontal scroll position survive untouched - only the decoded
    // thumbnails are handed back.
    const renderEnd = isRowNear ? Math.min(Math.max(reachedEnd, windowEnd), games.length - 1) : -1;
    const virtualItems = [];
    for (let i = 0; i <= renderEnd; i++) {
      virtualItems.push({ index: i, start: i * itemWidth, size: itemWidth });
    }

    return (
      <div
        className={styles.virtualizedContainer}
        data-has-jackpot={hasJackpotPanel}
        data-game-card-size={gameCardSize}
        data-section-name={sectionName}
        open-type={openType}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          ref={scrollElementRef}
          className={styles.virtualizedList}
          // data-grid-scroll-container marks the actual scrollable element so
          // outer consumers (e.g. Section.tsx resetting scrollLeft on Hot/Cold
          // toggle) can find it without an imperative-handle contract. The outer
          // `ref` div is not the scroller — the virtualizer's getScrollElement
          // returns this inner div.
          data-grid-scroll-container=""
          style={{ height: listHeight, width: '100%', overflow: 'auto', position: 'relative' }}
        >
          <div
            style={{
              height: '100%',
              width: virtualizer.getTotalSize() - getGap() + getPadding().start + getPadding().end,
              position: 'relative',
            }}
          >
            {virtualItems.map((virtualItem) => (
              <GameItem
                key={virtualItem.index}
                index={virtualItem.index}
                games={games}
                sectionName={sectionName}
                isLast={virtualItem.index === games.length - 1}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: virtualItem.start + getPadding().start,
                  width: virtualItem.size,
                  height: '100%',
                  transform: 'translateX(0px)',
                }}
              />
            ))}
          </div>
        </div>

      {scrollArrows.isHovered && (
        <>
          {scrollArrows.showLeftArrow && <LeftArrow onClick={scrollLeft} />}
          {scrollArrows.showRightArrow && <RightArrow onClick={scrollRight} />}
        </>
      )}
    </div>
  );
};

ReactVirtualGrid.displayName = 'ReactVirtualGrid';

export default memo(ReactVirtualGrid);
